/**
 * Convert #RRGGBB (or RRGGBB) to space-separated H S% L% for Tailwind/CSS `hsl(var(--token))`.
 */
export function brandingHexToHslCss(hex: string): string | null {
  try {
    const h = hex.replace('#', '');
    if (h.length !== 6) return null;

    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hh = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          hh = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          hh = ((b - r) / d + 2) / 6;
          break;
        case b:
          hh = ((r - g) / d + 4) / 6;
          break;
      }
    }

    const hDeg = Math.round(hh * 360);
    const sPct = Math.round(s * 100);
    const lPct = Math.round(l * 100);

    return `${hDeg} ${sPct}% ${lPct}%`;
  } catch {
    return null;
  }
}

/** #RRGGBB (or RRGGBB) to rgba() for solid / translucent backgrounds (e.g. scrolled nav). */
export function brandingHexToRgba(hex: string, alpha: number): string | null {
  try {
    const h = hex.replace('#', '');
    if (h.length !== 6 || alpha < 0 || alpha > 1) return null;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return null;
  }
}

/** #RRGGBB (or RRGGBB) → `[r, g, b]` integer triple for jsPDF / canvas APIs. */
export function brandingHexToRgbArray(hex: string): [number, number, number] | null {
  try {
    const h = hex.replace('#', '');
    if (h.length !== 6) return null;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return [r, g, b];
  } catch {
    return null;
  }
}

/** #RRGGBB (or RRGGBB) → 6-char uppercase ARGB string used by ExcelJS (e.g. "0B5E3B"). */
export function brandingHexToExcelArgb(hex: string): string | null {
  try {
    const h = hex.replace('#', '').toUpperCase();
    if (h.length !== 6) return null;
    if (!/^[0-9A-F]{6}$/.test(h)) return null;
    return h;
  } catch {
    return null;
  }
}

/**
 * Mix `hex` with white at the given strength (0 = pure hex, 1 = white). Used to
 * derive a "primaryLight" tint from a single brand primary color.
 */
export function brandingMixWithWhite(hex: string, strength: number): string | null {
  const rgb = brandingHexToRgbArray(hex);
  if (!rgb) return null;
  const s = Math.max(0, Math.min(1, strength));
  const r = Math.round(rgb[0] + (255 - rgb[0]) * s);
  const g = Math.round(rgb[1] + (255 - rgb[1]) * s);
  const b = Math.round(rgb[2] + (255 - rgb[2]) * s);
  return (
    r.toString(16).padStart(2, '0').toUpperCase() +
    g.toString(16).padStart(2, '0').toUpperCase() +
    b.toString(16).padStart(2, '0').toUpperCase()
  );
}

/** Linearize an sRGB channel (0–1) for WCAG relative luminance. */
function srgbChannelToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#RRGGBB` / `RRGGBB` (0 = black, 1 = white). */
export function brandingHexRelativeLuminance(hex: string): number | null {
  const rgb = brandingHexToRgbArray(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((n) => srgbChannelToLinear(n / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** True when brand hex is light enough that white-on-color chrome fails contrast. */
export function isLightBrandHex(hex: string, threshold = 0.6): boolean {
  const L = brandingHexRelativeLuminance(hex);
  return L !== null && L >= threshold;
}

/**
 * HSL channel string for `--nav-chrome-fg` (used by `variant="icon"`).
 * Light backgrounds → black; dark / missing → white (Entravia default).
 */
export function navChromeForegroundHslChannels(bgHex: string | null | undefined): string {
  if (bgHex && isLightBrandHex(bgHex)) {
    return '0 0% 0%';
  }
  return '0 0% 100%';
}
